import {
  type PeerMetadata,
  type ProcessLaunchPackageAndroid,
} from '@playcastdotio/Messaging';
import type {
  IPlayjectorService,
  InstanceLaunchedEvent,
} from '../../../services/playjector/interfaces/IPlayjectorService';
import type { GameInstanceAllocator } from '../GameInstanceAllocator';
import type {
  GameInstanceSessionRecord,
  GameInstanceStore,
} from '../GameInstanceStore';
import { resolveInstanceCamera } from '../../media/cameraNames';
import { AndroidInstanceLaunchManager } from './AndroidInstanceLaunchManager';

export type GameInstanceLaunchResult = {
  sessionId: string;
  gameInstanceIndex: number;
  succeeded: boolean;
  failure?: string;
};

type PendingReply = {
  resolve: (result: 'SUCCESS' | 'FAILURE') => void;
  message?: string;
};

// Re-exported so existing importers keep working after the Phase 8 (A6)
// extraction of the android launch FSM to AndroidInstanceLaunchManager.
export {
  DEFAULT_ANDROID_LAUNCH_REPLY_TIMEOUT_MS,
  computeAndroidLaunchReplyTimeoutMs,
} from './AndroidInstanceLaunchManager';

/**
 * Manages game instance lifecycle: display creation, launcher invocation,
 * result correlation, and teardown. Each instance launches independently;
 * failures are scoped to the failing instance only.
 *
 * The android multi-instance launch FSM lives in the sibling
 * `AndroidInstanceLaunchManager` (Phase 8, review fix A6); the android-facing
 * methods below delegate to it so this class's public API is unchanged.
 */
export class GameInstanceLaunchManager {
  private readonly pendingReplies = new Map<string, PendingReply>();
  private readonly androidLaunchManager: AndroidInstanceLaunchManager;

  constructor(
    private readonly service: IPlayjectorService,
    private readonly store: GameInstanceStore,
    private readonly allocator: GameInstanceAllocator,
    private readonly onRequiredCamerasChanged: (cameras: readonly string[]) => void =
      () => undefined,
    private readonly onSessionsChanged: (sessions: GameInstanceSessionRecord[]) => void =
      () => undefined,
  ) {
    this.androidLaunchManager = new AndroidInstanceLaunchManager(
      service,
      store,
      allocator,
      (cameras) => this.onRequiredCamerasChanged(cameras),
      (sessions) => this.onSessionsChanged(sessions),
    );
  }

  /**
   * Route a pavilionLaunchReply to the pending promise for that sessionId.
   * Returns true if the reply was matched, false if no pending request exists.
   */
  public handleLaunchReply(sessionId: string, resultType: 'SUCCESS' | 'FAILURE', message?: string): boolean {
    const pending = this.pendingReplies.get(sessionId);
    if (!pending) return false;
    pending.message = message;
    pending.resolve(resultType);
    this.pendingReplies.delete(sessionId);
    return true;
  }

  // ========================================
  // Android multi-instance path (android-multi-instance-host Phase 3)
  //
  // One `launchAndroidInstances` call per campaign launch — no per-session
  // processLaunch, no `prepareDisplays`/`createVirtualDisplayForSession`
  // (C++ owns the monitor/display/VM lifecycle). The launch FSM lives in
  // AndroidInstanceLaunchManager (Phase 8, review fix A6); these methods
  // delegate so callers keep one launch-manager surface.
  // ========================================

  /**
   * Reserve index-only allocations, transition them to `launching`, send the
   * single android launch package, and resolve every expected instance from
   * its `instanceLaunched` reply (or the reply timeout). Per-instance failures
   * are scoped to the failing instance; allocation/package failures throw
   * before any IPC is sent.
   */
  public async launchInstances(
    pkg: ProcessLaunchPackageAndroid,
    options: { campaignId: string; replyTimeoutMs?: number },
  ): Promise<GameInstanceLaunchResult[]> {
    return this.androidLaunchManager.launchInstances(pkg, options);
  }

  /**
   * Route an `instanceLaunched` reply to the pending wait for that
   * (`streamId`, `gameInstanceIndex`) pair. Returns true when matched; false
   * for stale/late replies (already timed out or unknown stream) — a safe
   * no-op, matching `handleLaunchReply`.
   */
  public handleInstanceLaunched(event: InstanceLaunchedEvent): boolean {
    return this.androidLaunchManager.handleInstanceLaunched(event);
  }

  /** Stream id of the active android launch (undefined after cleanup). */
  public getActiveAndroidStreamId(): string | undefined {
    return this.androidLaunchManager.getActiveAndroidStreamId();
  }

  /**
   * Fail the active android stream's pending instance waiters with `reason`.
   * Lets an aggregate whole-launch FAILURE reply (empty `sessionId`) resolve
   * every waiter fast with the real reason instead of the per-instance timeout.
   */
  public failActiveAndroidStreamLaunches(streamId: string, reason: string): void {
    this.androidLaunchManager.failActiveStreamLaunches(streamId, reason);
  }

  /** Tear down a single instance by gameInstanceIndex. Other instances are untouched. */
  public async teardownInstance(gameInstanceIndex: number, hostAuthId: string): Promise<void> {
    const session = this.store.getSessionByIndex(gameInstanceIndex);
    if (!session) return;
    await this.teardownSession(session, hostAuthId);
  }

  /** Tear down all instances. */
  public async cleanup(hostAuthId: string): Promise<void> {
    const sessions = this.store.getSessions();
    await Promise.allSettled(
      sessions.map((session) => this.teardownSession(session, hostAuthId)),
    );
    this.pendingReplies.clear();
    // Android path: unblock any in-flight instance waits so `launchInstances`
    // settles instead of hanging until the reply timeout, and unregister the
    // torn-down stream on the service (Phase 8, review fix S1).
    this.androidLaunchManager.abortPendingLaunches();
    this.onRequiredCamerasChanged([]);
  }

  public getSessions(): GameInstanceSessionRecord[] {
    return this.store.getSessions();
  }

  public getReadySessions(): GameInstanceSessionRecord[] {
    return this.store.getSessions().filter((s) => s.status === 'ready');
  }

  /**
   * Build peer metadata for a specific session. Used by PeerCoordinator to
   * map an instance to its media bindings.
   *
   * Android multi-instance host Phase 5: the payload always carries
   * `gameInstanceIndex` (the canonical instance identity InputManager stamps
   * onto android-path input) and resolves the camera echo-preferred via
   * `resolveInstanceCamera`. C++-internal handles (vmId, display serial/GUID/
   * target, ports, display geometry) ride along only when actually allocated —
   * the legacy mumu path. Android records are index-only allocations, so their
   * payloads never carry those handles off the machine.
   */
  public toPeerSession(session: GameInstanceSessionRecord): NonNullable<PeerMetadata['playjectorSession']> {
    const peerSession: NonNullable<PeerMetadata['playjectorSession']> = {
      sessionId: session.sessionId,
      gameInstanceIndex: session.gameInstanceIndex,
      shadowCamName: resolveInstanceCamera(session),
      virtualDisplayName: session.virtualDisplayName,
    };
    if (session.playjectorPort !== undefined) peerSession.playjectorPort = session.playjectorPort;
    if (session.loggingPort !== undefined) peerSession.loggingPort = session.loggingPort;
    if (session.virtualDisplayGuid !== undefined) peerSession.virtualDisplayGuid = session.virtualDisplayGuid;
    if (session.virtualDisplaySerial !== undefined) peerSession.virtualDisplaySerial = session.virtualDisplaySerial;
    if (session.virtualDisplayGdiDeviceName !== undefined) {
      peerSession.virtualDisplayGdiDeviceName = session.virtualDisplayGdiDeviceName;
    }
    if (session.virtualDisplayAdapterLuid !== undefined) {
      peerSession.virtualDisplayAdapterLuid = session.virtualDisplayAdapterLuid;
    }
    if (session.virtualDisplayTargetId !== undefined) {
      peerSession.virtualDisplayTargetId = session.virtualDisplayTargetId;
    }
    if (session.audioDeviceName !== undefined) peerSession.audioDeviceName = session.audioDeviceName;
    if (session.displayWidth !== undefined) peerSession.displayWidth = session.displayWidth;
    if (session.displayHeight !== undefined) peerSession.displayHeight = session.displayHeight;
    if (session.refreshRate !== undefined) peerSession.refreshRate = session.refreshRate;
    return peerSession;
  }

  private async teardownSession(session: GameInstanceSessionRecord, hostAuthId: string): Promise<void> {
    if (session.status !== 'stopping') {
      this.store.transition(session.sessionId, 'stopping');
      this.notifySessionsChanged();
    }
    try {
      if (session.virtualDisplayGuid) {
        this.service.removeVirtualDisplay(hostAuthId, session.virtualDisplayGuid);
      }
    } catch (error) {
      console.warn('[GameInstanceLaunchManager] Session cleanup incomplete', session.sessionId, error);
    } finally {
      this.pendingReplies.delete(session.sessionId);
      this.store.release(session.sessionId);
      this.notifySessionsChanged();
    }
  }

  private requiredCameras(): string[] {
    return this.store.getSessions()
      .map((s) => s.shadowCamName)
      .filter((camera): camera is string => Boolean(camera));
  }

  private notifySessionsChanged(): void {
    this.onSessionsChanged(this.store.getSessions());
  }
}
